import React, { useState } from 'react';
import { Typography } from 'antd';

const { Title, Text } = Typography;

export interface AuthFormCardProps {
  /** Shown under the logo */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Tailwind width classes for the card column */
  maxWidthClass?: string;
  children: React.ReactNode;
}

/**
 * Right-hand panel background + white card with top brand stripe and TOC logo.
 */
export const AuthFormCard: React.FC<AuthFormCardProps> = ({
  title,
  subtitle,
  maxWidthClass = 'max-w-[400px] md:max-w-[420px] lg:max-w-[400px] xl:max-w-[432px]',
  children,
}) => {
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <div
      className="relative flex w-full flex-1 flex-col justify-center px-4 py-10 pb-[max(2.5rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-12 md:px-10 md:py-14 lg:min-h-0 lg:h-full lg:w-[48%] lg:justify-center lg:overflow-y-auto lg:px-8 lg:py-10 xl:w-1/2 xl:px-12"
      style={{ background: 'linear-gradient(160deg, #f1f5f9 0%, #e8f0fe 42%, #f8fafc 100%)' }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden opacity-70"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 100% -10%, rgba(1, 113, 191, 0.12), transparent), radial-gradient(ellipse 60% 40% at -10% 100%, rgba(247, 148, 31, 0.08), transparent)',
        }}
      />

      <div className={`auth-fade-in-up relative z-[1] mx-auto w-full ${maxWidthClass}`}>
        <article className="overflow-hidden rounded-lg border border-slate-200/90 bg-white shadow-[0_4px_6px_-1px_rgba(15,23,42,0.06),0_20px_50px_-12px_rgba(1,113,191,0.12)] ring-1 ring-slate-900/[0.04]">
          <div className="h-1 w-full bg-gradient-to-r from-[#0171BF] via-[#0a6cbd] to-[#F7941F]" aria-hidden />

          <div className="px-6 py-7 sm:px-8 sm:py-8">
            <div className="mb-6 flex justify-center sm:mb-7">
              {!logoFailed ? (
                <img
                  src="/logo.png"
                  alt="The Old Cart"
                  className="h-10 w-auto max-w-[200px] object-contain object-center sm:h-11"
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <div className="flex items-baseline text-2xl font-bold tracking-tight text-slate-900 sm:text-[1.65rem]">
                  <span className="text-[#F7941F]">T</span>
                  <span className="text-[#0171BF]">he Old Cart</span>
                </div>
              )}
            </div>

            {(title != null || subtitle != null) && (
              <header className="mb-7 text-left sm:mb-8">
                {title != null &&
                  (typeof title === 'string' ? (
                    <Title
                      level={3}
                      className="!mb-2 !mt-0 !text-[1.375rem] !font-semibold !leading-tight !tracking-tight !text-slate-900 sm:!text-2xl"
                    >
                      {title}
                    </Title>
                  ) : (
                    <div className="mb-2">{title}</div>
                  ))}
                {subtitle != null &&
                  (typeof subtitle === 'string' ? (
                    <Text className="m-0 text-sm leading-relaxed text-slate-600">{subtitle}</Text>
                  ) : (
                    <div className="text-sm leading-relaxed text-slate-600">{subtitle}</div>
                  ))}
              </header>
            )}

            {children}
          </div>
        </article>
      </div>
    </div>
  );
};
